import React, { useState, useEffect, useMemo } from 'react';
import { FileDown, X, Search, Check, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet } from '../../lib/api.js';

export default function CsvExportWizard({ ids, totalFiltered, baseParams, onClose }) {
  const [catalog, setCatalog] = useState([]);
  const [selectedCols, setSelectedCols] = useState(new Set());
  const [includeAllCustom, setIncludeAllCustom] = useState(false);
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(false);

  const DEFAULT_SET = useMemo(() => new Set([
    'id', 'firstName', 'lastName', 'email', 'phone', 'altPhone',
    'state', 'city', 'zip', 'timezone',
    'status', 'priority', 'dispositionTags',
    'vendorName', 'assignedAgentName', 'assignedAgentEmail',
    'productInterest', 'sourceCampaign', 'saleAmount',
    'tags', 'createdAt', 'updatedAt', 'lastContactedAt',
  ]), []);

  useEffect(() => {
    let alive = true;
    apiGet('/leads/export/catalog')
      .then((r) => { if (alive) { setCatalog(r.columns || []); setSelectedCols(new Set(DEFAULT_SET)); } })
      .catch(() => toast.error('Failed to load column catalog'));
    return () => { alive = false; };
  }, [DEFAULT_SET]);

  const grouped = useMemo(() => {
    const acc = {};
    catalog.forEach((c) => {
      if (search && !c.label.toLowerCase().includes(search.toLowerCase())) return;
      if (!acc[c.group]) acc[c.group] = [];
      acc[c.group].push(c);
    });
    return acc;
  }, [catalog, search]);

  const toggle = (v) => {
    const next = new Set(selectedCols);
    next.has(v) ? next.delete(v) : next.add(v);
    setSelectedCols(next);
  };
  const setAll = (cols) => setSelectedCols(new Set(cols));
  const presetDefault = () => setSelectedCols(new Set(DEFAULT_SET));
  const presetAll = () => setSelectedCols(new Set(catalog.map((c) => c.value)));
  const presetMin = () => setSelectedCols(new Set(['firstName', 'lastName', 'email', 'phone']));
  const clearAll = () => setSelectedCols(new Set());

  const generate = async () => {
    if (selectedCols.size === 0) {
      toast.error('Select at least one column');
      return;
    }
    setDownloading(true);
    try {
      const tok = localStorage.getItem('civix_token');
      const params = new URLSearchParams({
        ...baseParams,
        columns: [...selectedCols].join(','),
        includeAllCustom: includeAllCustom ? 'true' : 'false',
      });
      if (ids) params.set('ids', [...ids].join(','));
      const url = `${import.meta.env.VITE_API_URL || '/api'}/leads/export.csv?${params.toString()}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `civix-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success('CSV ready');
      onClose();
    } catch (_e) {
      toast.error('CSV download failed');
    } finally {
      setDownloading(false);
    }
  };

  const targetCount = ids ? ids.size : totalFiltered;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-civix-navy border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileDown className="w-5 h-5 text-civix-cyan" /> Generate CSV
            </h2>
            <div className="text-xs text-white/50 mt-1">
              {targetCount.toLocaleString()} {targetCount === 1 ? 'lead' : 'leads'} • {selectedCols.size} columns selected
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-white/10 shrink-0 flex flex-wrap items-center gap-2">
          <button onClick={presetDefault} className="text-[11px] px-2 py-1 rounded-sm bg-civix-purple/20 text-civix-purpleSoft hover:bg-civix-purple/30">
            Ringy default
          </button>
          <button onClick={presetAll} className="text-[11px] px-2 py-1 rounded-sm bg-white/5 text-white/70 hover:bg-white/10">
            Select all
          </button>
          <button onClick={presetMin} className="text-[11px] px-2 py-1 rounded-sm bg-white/5 text-white/70 hover:bg-white/10">
            Minimal
          </button>
          <button onClick={clearAll} className="text-[11px] px-2 py-1 rounded-sm bg-white/5 text-white/70 hover:bg-white/10">
            Clear
          </button>
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search columns…"
              className="input pl-7 py-1 text-xs w-48"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
          {Object.keys(grouped).length === 0 ? (
            <div className="text-center text-white/40 py-12 text-sm">
              No columns match "{search}"
            </div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-wide text-white/50 font-bold">{group}</div>
                  <button
                    onClick={() => setAll([...selectedCols, ...items.map((i) => i.value)])}
                    className="text-[10px] text-civix-cyan hover:underline"
                  >
                    Add all
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {items.map((c) => {
                    const on = selectedCols.has(c.value);
                    return (
                      <button
                        key={c.value}
                        onClick={() => toggle(c.value)}
                        className={`text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${
                          on ? 'bg-civix-purple/20 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'
                        }`}
                      >
                        <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                          on ? 'bg-civix-cyan border-civix-cyan' : 'border-white/30'
                        }`}>
                          {on && <Check className="w-2.5 h-2.5 text-civix-navy" />}
                        </span>
                        <span className="truncate">{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-white/10 px-5 py-3 flex items-center justify-between gap-3 shrink-0">
          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
            <input
              type="checkbox"
              checked={includeAllCustom}
              onChange={(e) => setIncludeAllCustom(e.target.checked)}
              className="rounded-sm border-white/20 bg-transparent"
            />
            Include all custom fields
          </label>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-ghost text-xs px-3 py-1.5">Cancel</button>
            <button
              onClick={generate}
              disabled={downloading || selectedCols.size === 0}
              className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              {downloading ? 'Generating…' : 'Generate CSV'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
